// components/secure/EscrowExamBundleSection.js — Examination pack → Download full examination bundle.
//
// Sits inside EscrowExamPackPanel and uses its period and accounts. Starts a bundle
// (GET /api/escrow/exam-bundle/new — a GET because examiner logins are read-only),
// shows its progress while the server builds it, then offers the ZIP. Bundles stay
// on the server for 24 hours; the list shows the recent ones (an examiner sees only
// their own — the server decides).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const btn = 'px-3 py-1.5 text-sm bg-slate-800 text-white rounded disabled:bg-indigo-300';
const link = 'text-indigo-700 underline disabled:opacity-50';
const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const size = (b) => (b ? (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`) : '');
const SET_NAMES = {
    licensee_roster: 'licensee roster', escrow_statements: 'bank statements', escrow_procedure: 'written escrow procedure',
    deposit_slips: 'deposit slips and cheque images', ledger_cards: 'ledger cards', signatories: 'signatories',
    division_notices: 'Division notices', record_incidents: 'incident log',
};
const missingText = (list) => list.map((s) => {
    const [k, why] = s.split(': ');
    return `${SET_NAMES[k] || k} (${why === 'not held' ? 'not held in the portal' : why === 'withheld' ? 'withheld — no Licensees grant' : 'not available'})`;
}).join('; ');

export default function EscrowExamBundleSection({ query, onLogged }) {
    const api = useApiClient();
    const [jobs, setJobs] = useState([]);
    const [active, setActive] = useState(null);
    const [error, setError] = useState('');
    const [notInstalled, setNotInstalled] = useState('');
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);

    const loadJobs = useCallback(() => api.get('/api/escrow/exam-bundle/jobs')
        .then((r) => { setJobs(r.jobs || []); setNotInstalled(r.not_installed ? r.message : ''); })
        .catch(() => {}), [api]);

    useEffect(() => { loadJobs(); return () => clearTimeout(timer.current); }, [loadJobs]);

    const poll = useCallback((id) => {
        clearTimeout(timer.current);
        api.get(`/api/escrow/exam-bundle/jobs/${id}`).then((j) => {
            setActive(j);
            if (j.status === 'queued' || j.status === 'running') timer.current = setTimeout(() => poll(id), 1500);
            else { loadJobs(); if (onLogged) onLogged(); }
        }).catch((e) => setError(e.message));
    }, [api, loadJobs, onLogged]);

    const start = async () => {
        setBusy(true); setError('');
        try {
            const r = await api.get(`/api/escrow/exam-bundle/new${query()}`);
            setActive({ job_id: r.job_id, status: 'queued', progress: 0, progress_label: 'Queued' });
            poll(r.job_id);
        } catch (e) {
            if (e.status === 409 && e.payload?.job) { setActive(e.payload.job); poll(e.payload.job.job_id); }
            else setError(e.status === 403 ? `${e.message} (the bundle sits in the "Escrow and special accounts" area).` : e.message);
        } finally { setBusy(false); }
    };

    const download = async (job) => {
        setError('');
        try {
            const r = await fetch(`/api/escrow/exam-bundle/jobs/${job.job_id}/download`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
            if (!r.ok) {
                let m = 'Could not download the bundle.';
                try { m = (await r.json()).message || m; } catch { /* not JSON */ }
                throw new Error(m);
            }
            const url = URL.createObjectURL(await r.blob());
            const a = document.createElement('a');
            a.href = url; a.download = job.file_name || 'IDFPR-Examination-Bundle.zip';
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            loadJobs();
        } catch (e) { setError(e.message); loadJobs(); }
    };

    const running = active && (active.status === 'queued' || active.status === 'running');

    return (
        <div className="border rounded p-3 mb-3 bg-slate-50">
            <div className="flex flex-wrap items-center gap-3">
                <button className={btn} disabled={busy || running || !!notInstalled} onClick={start}>Download full examination bundle</button>
                <span className="text-xs text-slate-500">
                    One ZIP for the period and accounts above: an index citing file and page for each examination item, then the
                    journal, a ledger card per transaction, the reconciliations, deposit and disbursement registers, signatories,
                    Division notices, incident log and licensee roster — each as PDF and CSV. Account numbers show the last four
                    digits only; no SSNs or tax IDs.
                </span>
            </div>
            {notInstalled && <p className="text-sm text-amber-700 mt-2">{notInstalled}</p>}
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}

            {active && (
                <div className="mt-2 text-sm">
                    {running && (
                        <>
                            <div className="w-full bg-slate-200 rounded h-2 mb-1">
                                <div className="bg-slate-800 h-2 rounded" style={{ width: `${Math.max(3, active.progress || 0)}%` }} />
                            </div>
                            <span className="text-slate-600">{active.progress || 0}% — {active.progress_label || 'Working'}</span>
                        </>
                    )}
                    {active.status === 'ready' && (
                        <span className="text-green-700">
                            Ready: <button className={link} onClick={() => download(active)}>{active.file_name}</button> ({size(active.file_bytes)}),
                            available until {when(active.expires_at && `${String(active.expires_at).replace(' ', 'T').replace(/Z?$/, 'Z')}`)}.
                            {active.sets_missing?.length ? <span className="text-amber-700"> Not in the bundle: {missingText(active.sets_missing)} — the index says why.</span> : null}
                        </span>
                    )}
                    {active.status === 'failed' && <span className="text-red-600">The bundle failed: {active.error_message}</span>}
                </div>
            )}

            {jobs.length > 0 && (
                <details className="mt-2 text-xs text-slate-600">
                    <summary className="cursor-pointer">Recent bundles ({jobs.length})</summary>
                    <ul className="mt-1 space-y-0.5">
                        {jobs.map((j) => (
                            <li key={j.job_id}>
                                #{j.job_id} · {when(j.generated_at)} · {j.generated_by_name}{j.generated_by_role === 'examiner' ? ' (examiner)' : ''} ·{' '}
                                {j.period_from || j.period_to ? `${j.period_from || 'start'} to ${j.period_to || 'today'}` : 'all records'}
                                {j.account_ids ? ` · accounts ${j.account_ids}` : ''} ·{' '}
                                {j.status === 'ready'
                                    ? <button className={link} onClick={() => download(j)}>download ({size(j.file_bytes)}, {j.downloaded_count}× so far)</button>
                                    : j.status === 'failed' ? <span className="text-red-600">failed</span>
                                        : j.status === 'expired' ? 'expired' : `${j.progress}%`}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}
